// ─── Request steps ────────────────────────────────────────────────────────────
// What the step loader (Common/LoadingSteps) draws: the list requests in flight
// on the current page, one line each. WebServiceAmazon.getAPI reports every GET
// here. Only a path with an entry in describeRequest is kept, so writes and the
// small background reads (the menu's pending count, the header's user lookups)
// never show.
//
// The detail line says what the server does for that request, read from the
// backend handlers (docs/slow-list-apis.md). The slow handlers also report the
// step they are on (backend Utility/Progress.js); while those arrive they are
// drawn in place of the detail line.

export type StepStatus = "pending" | "done" | "failed";

// A step the server reported for a request: "Sponsored Products campaigns
// from Amazon", active, "1,200 so far". Each report carries the step's whole
// state, so the latest one per id is the step.
export interface ServerStep {
  id: string;
  label: string;
  state: "active" | "done" | "failed";
  detail?: string;
}

export interface StepRequest {
  id: number;
  // Requests with the same key share one line (a refetch, a duplicate).
  key: string;
  label: string;
  detail?: string;
  // The page it was sent from: a request still in flight after the user has
  // moved on is not what the new page is waiting for.
  route: string;
  startedAt: number;
  endedAt?: number;
  status: StepStatus;
  // In the order the server started them.
  serverSteps?: ServerStep[];
}

interface StepLabel {
  key: string;
  label: string;
  detail?: string;
}

const AMAZON_LIVE = "Read live from Amazon";

// Labels by path. A path that is not here is not tracked.
const describeRequest = (action: string): StepLabel | null => {
  const [rawPath, query = ""] = action.split("?");
  const path = rawPath.replace(/^\/+/, "").replace(/\/+$/, "");
  const params = new URLSearchParams(query);
  const has = (name: string) => !!params.get(name);

  switch (path) {
    // Amazon shell
    case "amazon/user/amazon-profiles":
      return { key: path, label: "Your Amazon accounts" };
    case "amazon/portfolios":
      return has("start_date")
        ? { key: path, label: "Portfolios", detail: "With spend and sales for your dates" }
        : { key: path, label: "Portfolios" };

    // Campaigns and ad groups
    case "amazon/campaigns":
      return has("campaignId")
        ? { key: `${path}#one`, label: "Campaign details", detail: AMAZON_LIVE }
        : {
            key: path,
            label: "Campaigns",
            detail: "All ad types read live from Amazon, then matched with performance",
          };
    case "amazon/campaigns/graph-v2":
    case "amazon/groups/graph-v2":
    case "amazon/search-term/graph":
    case "amazon/product-report-graph":
      return { key: path, label: "Performance chart" };
    case "amazon/overview":
      return { key: path, label: "Summary totals" };
    case "amazon/groups":
      return has("ad_group_id")
        ? { key: `${path}#one`, label: "Ad group details", detail: AMAZON_LIVE }
        : {
            key: path,
            label: "Ad groups",
            detail: "Campaigns, then their ad groups, read live from Amazon",
          };

    // Campaign and ad group tabs
    case "amazon/campaign/ad-group/targeting":
      return {
        key: path,
        label: "Keywords and targets",
        detail: "Performance from reports, current bids read live from Amazon",
      };
    case "amazon/campaign/ad-group/suggested-bids":
      return { key: path, label: "Suggested bids", detail: "Priced live by Amazon, in batches" };
    case "amazon/campaign/ad-product/targeting":
      return { key: path, label: "Ads", detail: "Performance from reports, ad status checked with Amazon" };
    case "amazon/campaign/ad-group/setting/nagetive-keywords":
    case "amazon/campaign/setting/nagetive-keywords":
      return { key: path, label: "Negative keywords", detail: AMAZON_LIVE };
    case "amazon/campaign/ad-group/setting/nagetive-products":
      return { key: path, label: "Negative products", detail: AMAZON_LIVE };
    case "amazon/campaign/placements":
    case "amazon/campaign/sp-placements":
    case "amazon/campaign/sb-placements":
      return {
        key: path,
        label: "Placements",
        detail: "Bid adjustments read live from Amazon, then placement performance",
      };
    case "amazon/campaign/history":
      return { key: path, label: "Change history", detail: AMAZON_LIVE };
    case "amazon/sd/ad-group/creative":
      return { key: path, label: "Creative", detail: AMAZON_LIVE };

    // Report screens
    case "amazon/targeting/keywords":
      return { key: path, label: "Keywords", detail: "Totalling keyword performance for your dates" };
    case "amazon/search-term":
      return { key: path, label: "Search terms", detail: "Totalling search-term performance for your dates" };
    case "amazon/product-report-list":
      return params.get("type") === "SB_PRODUCT_ADS"
        ? { key: `${path}#sb`, label: "Sponsored Brands ads", detail: "Totalling ad performance for your dates" }
        : { key: path, label: "Products", detail: "Matching your catalogue with ad performance" };
    case "amazon/products-list":
      return { key: path, label: "Products", detail: "Totalling ad performance per ASIN" };
    case "amazon/campaigns/ams/heatmap":
      return { key: path, label: "Hourly heatmap", detail: "Performance by hour and weekday" };
    case "amazon/master-overview":
      return {
        key: path,
        label: "Dashboard totals",
        detail: "This period, the comparison period and the daily trend",
      };
    case "amazon/accounts":
      return { key: path, label: "Amazon accounts", detail: "With spend and sales for each account" };
    case "amazon/audit-logs":
      return { key: path, label: "Change log" };
    case "amazon/recommendations":
      return { key: path, label: "Recommendations" };
    case "amazon/recommendations/settings":
      return { key: path, label: "Recommendation settings" };

    // Walmart
    case "walmart/accounts":
      return { key: path, label: "Walmart accounts", detail: "With spend and sales for each account" };
    case "walmart/portfolios":
      return { key: path, label: "Portfolios" };
    case "walmart/campaigns":
      return has("campaignId")
        ? { key: `${path}#one`, label: "Campaign details", detail: "Read live from Walmart" }
        : { key: path, label: "Campaigns", detail: "Read live from Walmart, then matched with performance" };
    case "walmart/campaigns/graph":
    case "walmart/adGroups/graph":
      return { key: path, label: "Performance chart" };
    case "walmart/adGroups":
      return { key: path, label: "Ad groups", detail: "Read live from Walmart, then matched with performance" };
    case "walmart/adItems":
      return { key: path, label: "Ad items", detail: "Read live from Walmart, then matched with performance" };
    case "walmart/keywords":
      return { key: path, label: "Keywords", detail: "Read live from Walmart" };
    case "walmart/keyword-suggestions":
      return { key: path, label: "Suggested keywords", detail: "Read live from Walmart" };
    case "walmart/sba-profiles":
      return { key: path, label: "Brand profiles", detail: "Read live from Walmart" };

    default:
      return null;
  }
};

// ─── Store ────────────────────────────────────────────────────────────────────
// A plain module store, read with useSyncExternalStore. Every change replaces
// the array, so the snapshot is stable between changes.
let nextId = 1;
let requests: StepRequest[] = [];
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

export const subscribeSteps = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getStepsSnapshot = () => requests;

// Finished requests stay until the loader has shown them; this keeps the list
// from growing if a page fires requests with no loader on screen.
const STALE_AFTER_MS = 60000;

export type StepOutcome = "done" | "failed" | "aborted";

export interface RequestTracker {
  // False for a path the loader has no line for; the other two do nothing.
  tracked: boolean;
  serverStep: (step: ServerStep) => void;
  settle: (outcome: StepOutcome) => void;
}

const UNTRACKED: RequestTracker = { tracked: false, serverStep: () => {}, settle: () => {} };

// Called when a GET starts. serverStep is called for each step the server
// reports, and settle once the request settles. An aborted request is
// dropped: it was replaced by a newer one, or its page is gone, so nobody is
// waiting for it.
export const trackRequest = (action: string): RequestTracker => {
  const step = describeRequest(action);
  if (!step) return UNTRACKED;
  const now = Date.now();
  const entry: StepRequest = {
    id: nextId++,
    ...step,
    route: window.location.pathname,
    startedAt: now,
    status: "pending",
  };
  requests = [
    ...requests.filter((r) => r.status === "pending" || now - (r.endedAt ?? now) < STALE_AFTER_MS),
    entry,
  ];
  emit();

  let settled = false;
  return {
    tracked: true,
    serverStep: (reported) => {
      if (settled) return;
      requests = requests.map((r) => {
        if (r.id !== entry.id) return r;
        const steps = r.serverSteps ?? [];
        const at = steps.findIndex((s) => s.id === reported.id);
        return {
          ...r,
          serverSteps: at < 0 ? [...steps, reported] : steps.map((s, i) => (i === at ? reported : s)),
        };
      });
      emit();
    },
    settle: (outcome) => {
      if (settled) return;
      settled = true;
      requests =
        outcome === "aborted"
          ? requests.filter((r) => r.id !== entry.id)
          : requests.map((r) => (r.id === entry.id ? { ...r, status: outcome, endedAt: Date.now() } : r));
      emit();
    },
  };
};

// The loader calls this when a page has finished loading and it has shown the
// result: the next load on that page starts a fresh list.
export const clearFinishedSteps = () => {
  const next = requests.filter((r) => r.status === "pending");
  if (next.length === requests.length) return;
  requests = next;
  emit();
};
